import { useEffect, useState } from "react";
import { message } from "antd";
import { motion } from "framer-motion";
import { getmodule } from "../api/chatService";

// Simple reusable modal component
const CustomModal = ({
  isOpen,
  onClose,
  title,
  footer,
  maxWidth,
  children,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-lg p-6 w-full mx-4 ${
          maxWidth || "max-w-2xl"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="mb-6">{children}</div>

        {/* Footer */}
        {footer && <div className="flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
};

const FloatingModuleSelectionModal = ({ isOpen, onClose, onStartChat }) => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedModuleName, setSelectedModuleName] = useState(null);
  const [loadingModules, setLoadingModules] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchModules();
    }
  }, [isOpen]);

  const fetchModules = async () => {
    setLoadingModules(true);
    try {
      const token = localStorage.getItem("Token");
      const response = await getmodule(token);
      setModules(response.modules || []);
    } catch (error) {
      message.error("Failed to load modules");
      onClose();
    } finally {
      setLoadingModules(false);
    }
  };

  const handleCreate = () => {
    if (!selectedModule) {
      message.warning("Please select a module");
      return;
    }
    onStartChat(selectedModule, selectedModuleName);
    setSelectedModule(null);
    setSelectedModuleName(null);
  };

  const modalFooter = (
    <>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-skin-base bg-skin-fill border border-skin-base rounded-lg hover:bg-skin-fill-secondary focus:outline-none focus:ring-2 focus:ring-skin-base focus:ring-offset-2 transition-all duration-200"
      >
        Cancel
      </motion.button>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleCreate}
        disabled={!selectedModule}
        className={`px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
          selectedModule
            ? "bg-skin-button-base text-white hover:bg-skin-button-hover focus:ring-skin-base"
            : "bg-[var(--color-button-disabled)] text-[var(--color-button-disabled-text)] cursor-not-allowed"
        }`}
      >
        Start Chat
      </motion.button>
    </>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Module"
      footer={modalFooter}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-skin-muted text-center">
          Choose a module to start your conversation
        </p>

        {loadingModules ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-skin-base"></div>
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 ${
              modules.length >= 2 ? "sm:grid-cols-2" : "sm:grid-cols-1"
            } gap-4 max-h-80 overflow-y-auto scrollbar-thin px-2`}
          >
            {modules.map((module, index) => (
              <motion.button
                key={module.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setSelectedModule(module.id);
                  setSelectedModuleName(module.name);
                }}
                className={`w-full p-3 text-left rounded-lg border-2 transition-all duration-200
                  ${
                    selectedModule === module.id
                      ? "border-primary-500 ring-2 ring-primary-500/20 shadow-md bg-skin-card text-skin-base"
                      : index % 2 === 0
                      ? "bg-skin-card border-skin-base hover:bg-skin-card-hover hover:border-skin-hover text-skin-base"
                      : "bg-skin-accent border-skin-base hover:bg-skin-accent-hover hover:border-skin-hover text-skin-base"
                  }
                  group flex items-center justify-between`}
              >
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-skin-base group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors mb-1 text-sm">
                      {module.name}
                    </h3>
                    <p className="text-xs text-skin-muted line-clamp-2">
                      {module.desc}
                    </p>
                  </div>
                </div>

                {selectedModule === module.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3 h-3 rounded-full bg-primary-500 flex items-center justify-center ml-3 flex-shrink-0"
                  >
                    <div className="w-1 h-1 rounded-full bg-white" />
                  </motion.div>
                )}
              </motion.button>
            ))}
          </div>
        )}

        {!loadingModules && modules.length === 0 && (
          <div className="text-center py-8 text-skin-muted">
            <div className="text-2xl mb-2">📝</div>
            <div>No modules available</div>
            <div className="text-sm mt-1 opacity-75">
              Contact your administrator to add modules
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default FloatingModuleSelectionModal;
